/**
 * Global app header.
 *
 * Two clusters separated by a flexible spacer:
 *   * left:   workspace switcher
 *   * right:  notifications bell + user menu
 *
 * Behaviour:
 *   * The workspace switcher is a button that opens an MUI Menu listing
 *     every workspace the user has access to. Picking a workspace stores
 *     ``WORKSPACE_ID`` in localStorage (kept for backwards compat with
 *     existing components that read it directly) and then hard-navigates
 *     to ``/`` so the dashboard mounts fresh against the new context.
 *   * "Manage workspaces" jumps to ``/workspaces`` for create / edit /
 *     delete actions.
 *
 * Styling: the bar itself is a thin paper surface with a single bottom
 * divider and a soft backdrop blur — no heavy fills, no inline negative
 * positioning.
 */

import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { formatDistanceToNow } from '../../utils/datetime';

import { fetchRequest as logout } from '../../redux/actions/auth/logout';
import NotificationsBell from './NotificationsBell';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useTheme as useAppTheme } from '../../theme/ThemeContext';


// Map workspace role -> tooltip + icon. Order matters: the most
// privileged role wins when an account doc accidentally carries
// multiple roles.
const ROLE_META = {
  owner:  { Icon: PersonIcon,     label: 'Owner'  },
  admin:  { Icon: ManageAccountsIcon, label: 'Admin'  },
  editor: { Icon: PeopleIcon,     label: 'Editor' },
  viewer: { Icon: VisibilityIcon, label: 'Viewer' },
  member: { Icon: PeopleIcon,     label: 'Member' },
};


// ---------------------------------------------------------------------------
// Workspace switcher
// ---------------------------------------------------------------------------

// "in 6 days" / "in 3 hours"; "now" once it has passed.
const timeUntil = (iso) => {
  try {
    const t = new Date(iso);
    if (t.getTime() <= Date.now()) return 'now';
    return `in ${formatDistanceToNow(t)}`;
  } catch (_e) {
    return '';
  }
};

const WorkspaceSwitcher = ({ workspaces, activeId, onPick, onManage }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const active = workspaces.find((w) => w.uid === activeId) || null;

  // Permanent workspaces first (as returned), then an "Ephemeral" group
  // sorted by soonest expiry.
  const groupedWorkspaces = React.useMemo(() => {
    const permanent = workspaces.filter((w) => !w.expires_at);
    const ephemeral = workspaces
      .filter((w) => w.expires_at)
      .sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at));
    return [...permanent, ...ephemeral];
  }, [workspaces]);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handlePick = (uid) => {
    handleClose();
    if (uid !== activeId) onPick(uid);
  };

  const handleManage = () => {
    handleClose();
    onManage();
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="text"
        disableRipple
        startIcon={<WorkspacesOutlinedIcon sx={{ fontSize: 18 }} />}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              transition: 'transform 160ms ease',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        }
        sx={{
          height: 36,
          maxWidth: { xs: 168, sm: 320 },
          px: 1.5,
          borderRadius: 1.5,
          textTransform: 'none',
          color: 'text.primary',
          fontWeight: 500,
          fontSize: 14,
          letterSpacing: 0,
          gap: 0.5,
          bgcolor: open
            ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08)
            : 'transparent',
          border: '1px solid',
          borderColor: open ? 'primary.main' : 'divider',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.06),
            borderColor: alpha(theme.palette.primary.main, 0.4),
          },
          '& .MuiButton-startIcon': { ml: 0, mr: 1, color: 'primary.main' },
          '& .MuiButton-endIcon': { ml: 0.5, mr: 0, color: 'text.secondary' },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: '0 1 auto',
            color: active ? 'text.primary' : 'text.secondary',
            fontStyle: active ? 'normal' : 'italic',
          }}
        >
          {active ? active.name : 'Select workspace'}
        </Box>
        {active?.expires_at && (
          <Tooltip
            title={`Ephemeral workspace - expires ${timeUntil(active.expires_at)}. Keep it from workspace settings.`}
            arrow
          >
            <HourglassBottomIcon sx={{ fontSize: 14, ml: 0.75, color: 'warning.main', flexShrink: 0 }} />
          </Tooltip>
        )}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.75,
              minWidth: 320,
              maxWidth: 380,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 6px 24px rgba(0,0,0,0.10)',
              p: 0.5,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            px: 1.25,
            pt: 0.75,
            pb: 0.5,
            color: 'text.secondary',
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            fontSize: 10,
          }}
        >
          Switch workspace
        </Typography>

        <Box sx={{ maxHeight: 360, overflowY: 'auto', py: 0.25 }}>
          {workspaces.length === 0 && (
            <Box sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                You don't have any workspaces yet.
              </Typography>
            </Box>
          )}
          {groupedWorkspaces.map((ws, index) => {
            const role = String(ws.role || '').toLowerCase();
            const meta = ROLE_META[role];
            const RoleIcon = meta?.Icon;
            const isActive = ws.uid === activeId;
            const ephemeral = Boolean(ws.expires_at);
            // Heading before the first ephemeral entry (they're sorted last).
            const showEphemeralHeading = ephemeral && (index === 0 || !groupedWorkspaces[index - 1].expires_at);
            return (
              <React.Fragment key={ws.uid}>
              {showEphemeralHeading && (
                <Tooltip
                  title="Ephemeral workspaces are deleted, with everything in them, when they expire. Keep one from its workspace settings."
                  placement="left"
                  arrow
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.25,
                      pt: 1,
                      pb: 0.25,
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      fontSize: 10,
                    }}
                  >
                    <HourglassBottomIcon sx={{ fontSize: 12 }} />
                    Ephemeral
                  </Typography>
                </Tooltip>
              )}
              <MenuItem
                onClick={() => handlePick(ws.uid)}
                selected={isActive}
                sx={{
                  borderRadius: 1,
                  mx: 0.5,
                  my: 0.25,
                  py: 0.75,
                  alignItems: 'center',
                  gap: 1,
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                  }}
                >
                  {isActive && <CheckIcon sx={{ fontSize: 16 }} />}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={isActive ? 600 : 500}
                    sx={{
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {ws.name}
                  </Typography>
                  {ephemeral && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', lineHeight: 1.2, fontSize: 10.5, color: 'warning.main' }}
                    >
                      expires {timeUntil(ws.expires_at)}
                    </Typography>
                  )}
                </Box>
                {RoleIcon && (
                  <Tooltip title={meta.label} placement="left" arrow>
                    <RoleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  </Tooltip>
                )}
              </MenuItem>
              </React.Fragment>
            );
          })}
        </Box>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={handleManage}
          sx={{
            borderRadius: 1,
            mx: 0.5,
            my: 0.25,
            py: 0.75,
            color: 'text.primary',
          }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            <SettingsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText
            primary="Manage workspaces"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};


// ---------------------------------------------------------------------------
// User menu
// ---------------------------------------------------------------------------

const UserMenu = ({ user, onLogout, onNavigate }) => {
  const theme = useTheme();
  const { isDarkMode, toggleTheme } = useAppTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Initials for the avatar. Falls back to a single character so the
  // avatar never renders blank.
  const initials = (() => {
    const name = String(user?.name || user?.email || '').trim();
    if (!name) return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || name[0].toUpperCase();
  })();

  // Everything account-related lives in the unified Settings shell,
  // so the menu stays minimal: identity -> Profile, Settings, theme,
  // log out.
  const items = [
    { label: 'Settings', icon: <SettingsIcon fontSize="small" />, path: '/settings/profile' },
  ];

  return (
    <>
      <Tooltip title="Account" arrow>
        <Box
          component="button"
          onClick={handleOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 36,
            px: 1,
            border: '1px solid',
            borderColor: open ? 'primary.main' : 'divider',
            borderRadius: 999,
            cursor: 'pointer',
            background: open
              ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08)
              : 'transparent',
            color: 'text.primary',
            transition: 'background-color 160ms ease, border-color 160ms ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.06),
              borderColor: alpha(theme.palette.primary.main, 0.4),
            },
          }}
          aria-label="Open account menu"
        >
          <Avatar
            sx={{
              width: 26,
              height: 26,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            {initials}
          </Avatar>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              maxWidth: 140,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: { xs: 'none', sm: 'inline' },
            }}
          >
            {user?.name || user?.email || 'Account'}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 16,
              color: 'text.secondary',
              transition: 'transform 160ms ease',
              transform: open ? 'rotate(180deg)' : 'none',
              display: { xs: 'none', sm: 'inline' },
            }}
          />
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.75,
              minWidth: 240,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 6px 24px rgba(0,0,0,0.10)',
              p: 0.5,
              overflow: 'hidden',
            },
          },
        }}
      >
        {/* Identity block — clickable, opens the Profile tab. */}
        <MenuItem
          onClick={() => { handleClose(); onNavigate('/settings/profile'); }}
          sx={{ borderRadius: 1, mx: 0.5, my: 0.25, px: 0.75, py: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.name || 'You'}
              </Typography>
              {user?.email && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.email}
                </Typography>
              )}
            </Box>
          </Stack>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {items.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => { handleClose(); onNavigate(item.path); }}
            sx={{ borderRadius: 1, mx: 0.5, my: 0.25, py: 0.75 }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            />
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={toggleTheme}
          sx={{ borderRadius: 1, mx: 0.5, my: 0.25, py: 0.75 }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
            {isDarkMode
              ? <DarkModeIcon fontSize="small" />
              : <LightModeIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary="Dark mode"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          />
          <Switch
            size="small"
            checked={isDarkMode}
            onChange={toggleTheme}
            onClick={(e) => e.stopPropagation()}
            inputProps={{ 'aria-label': 'Toggle dark mode' }}
          />
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={() => { handleClose(); onLogout(); }}
          sx={{
            borderRadius: 1,
            mx: 0.5,
            my: 0.25,
            py: 0.75,
            color: 'error.main',
            '&:hover': {
              bgcolor: alpha(theme.palette.error.main, 0.08),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Log out"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};


// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const Header = ({ payload }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Active workspace + the list come from the single source of truth.
  const { workspaces, activeWorkspaceId, switchWorkspace } = useWorkspace();

  const handleManageWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleLogout = () => {
    // The logout saga clears localStorage and redirects to /login.html once
    // cleanup is done. Navigating here would unload the page before the saga
    // finishes, leaving the auth token behind (which made logout require two
    // attempts).
    dispatch(logout());
  };

  return (
    <header className="header">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '100%',
          px: { xs: 1.25, sm: 2.5 },
          gap: { xs: 1, sm: 2 },
        }}
      >
        <WorkspaceSwitcher
          workspaces={workspaces}
          activeId={activeWorkspaceId}
          onPick={switchWorkspace}
          onManage={handleManageWorkspaces}
        />

        <Stack direction="row" alignItems="center" spacing={1}>
          <NotificationsBell />
          <UserMenu
            user={payload?.user}
            onLogout={handleLogout}
            onNavigate={navigate}
          />
        </Stack>
      </Box>
    </header>
  );
};

const mapStateToProps = (state) => ({
  payload:   state.user_getUser.response,
  isLoading: state.user_getUser.isLoading,
  error:     state.user_getUser.error,
});

export default connect(mapStateToProps)(Header);
